import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, Plus, ArrowLeft, Edit } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: '', association_name: '', association_city: '',
  purpose: '', progress_percentage: 0, category_ids: [], status: 'ACTIVE',
};

export default function AdminSocialProjects() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (user && user.admin_permissions !== undefined && !user.admin_permissions.includes('social_projects.manage')) navigate('/dashboard');
  }, [user]);

  const load = async () => {
    try {
      const [pr, cr] = await Promise.all([
        axios.get(`${API}/admin/social-projects`, { withCredentials: true }),
        axios.get(`${API}/social-categories`),
      ]);
      setProjects(pr.data || []);
      setCategories(cr.data || []);
    } catch (e) {
      toast.error('Errore caricamento');
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title, association_name: p.association_name,
      association_city: p.association_city, purpose: p.purpose,
      progress_percentage: p.progress_percentage,
      category_ids: p.category_ids || [], status: p.status,
    });
    setOpen(true);
  };
  const toggleCat = (id) => setForm(f => ({
    ...f, category_ids: f.category_ids.includes(id) ? f.category_ids.filter(x => x !== id) : [...f.category_ids, id],
  }));
  const save = async () => {
    const payload = {
      ...form,
      progress_percentage: Number(form.progress_percentage),
    };
    try {
      if (editing) {
        await axios.patch(`${API}/admin/social-projects/${editing.project_id}`, payload, { withCredentials: true });
        toast.success(t('asp_updated'));
      } else {
        await axios.post(`${API}/admin/social-projects`, payload, { withCredentials: true });
        toast.success(t('asp_created'));
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const catLabel = (id) => {
    const c = categories.find(x => x.category_id === id);
    return c ? (lang === 'en' ? c.label_en : c.label_it) : id;
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24" data-testid="admin-sp-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="mb-4"><ArrowLeft className="w-4 h-4 mr-1.5" />Admin</Button>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-primary" strokeWidth={1.5} />
            <h1 className="text-3xl font-serif font-bold text-primary">{t('asp_tab')}</h1>
          </div>
          <Button onClick={openNew} data-testid="asp-new"><Plus className="w-4 h-4 mr-1.5" />{t('asp_new')}</Button>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="p-0 overflow-x-auto">
            {projects.length === 0 ? (
              <p className="p-8 text-center text-muted-foreground font-sans">—</p>
            ) : (
              <table className="w-full">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left text-xs font-sans p-3">{t('asp_title')}</th>
                    <th className="text-left text-xs font-sans p-3">{t('asp_association')}</th>
                    <th className="text-left text-xs font-sans p-3">{t('asp_city')}</th>
                    <th className="text-left text-xs font-sans p-3">{t('asp_progress')}</th>
                    <th className="text-left text-xs font-sans p-3">{t('asp_status')}</th>
                    <th className="text-right text-xs font-sans p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => (
                    <tr key={p.project_id} className="border-t border-border" data-testid={`asp-row-${i}`}>
                      <td className="p-3 text-sm font-sans font-semibold">{p.title}</td>
                      <td className="p-3 text-sm font-sans">{p.association_name}</td>
                      <td className="p-3 text-sm font-sans text-muted-foreground">{p.association_city}</td>
                      <td className="p-3 text-sm font-sans">{p.progress_percentage}%</td>
                      <td className="p-3"><Badge variant={p.status === 'ACTIVE' ? 'default' : 'secondary'}>{p.status === 'ACTIVE' ? t('sp_status_active') : t('sp_status_completed')}</Badge></td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`asp-edit-${i}`}><Edit className="w-4 h-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? t('asp_edit') : t('asp_new')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t('asp_title')}</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} data-testid="asp-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('asp_association')}</Label><Input value={form.association_name} onChange={e => setForm(f => ({...f, association_name: e.target.value}))} data-testid="asp-association" /></div>
                <div><Label>{t('asp_city')}</Label><Input value={form.association_city} onChange={e => setForm(f => ({...f, association_city: e.target.value}))} data-testid="asp-city" /></div>
              </div>
              <div><Label>{t('asp_purpose')}</Label><Textarea rows={4} value={form.purpose} onChange={e => setForm(f => ({...f, purpose: e.target.value}))} data-testid="asp-purpose" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('asp_progress')}</Label>
                  <Input type="number" min={0} max={100} value={form.progress_percentage} onChange={e => setForm(f => ({...f, progress_percentage: e.target.value}))} data-testid="asp-progress" />
                </div>
                <div>
                  <Label>{t('asp_status')}</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                    <SelectTrigger data-testid="asp-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">{t('sp_status_active')}</SelectItem>
                      <SelectItem value="COMPLETED">{t('sp_status_completed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('asp_categories')}</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {categories.map(c => (
                    <label key={c.category_id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.category_ids.includes(c.category_id)} onCheckedChange={() => toggleCat(c.category_id)} />
                      {catLabel(c.category_id)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t('asp_cancel')}</Button>
              <Button onClick={save} data-testid="asp-save">{t('asp_save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
